<template>
  <div class="flex flex-col lg:flex-row">
    <ul>
      <li
        v-for="item in completedPipelineStages"
        :key="item.id"
        class="hidden lg:inline-block px-2 py-1 rounded-full bg-green-600 text-white ml-1 first:ml-0"
      >
        <FontAwesomeIcon icon="check" fixed-width />
      </li>
      <li
        v-if="pipelineStage"
        class="inline-block text-base mt-2 lg:mt-0 lg:px-3 lg:py-1 lg:bg-blue-600 lg:text-white lg:ml-1 rounded-full"
      >
        {{ pipelineStage.name }}
      </li>
      <li
        v-if="nextPipelineStage"
        class="hidden lg:inline-block px-3 py-1 bg-gray-300 ml-1 rounded-full"
      >
        {{ nextPipelineStage.name }}
      </li>
      <li
        v-if="pendingPipelineStagesCount"
        class="hidden lg:inline-block px-3 py-1 bg-gray-300 ml-1 rounded-full"
      >
        {{ pendingPipelineStagesCount }} more...
      </li>
    </ul>
    <div class="mt-4 lg:mt-0 ml-auto flex-none">
      <button
        v-if="pendingPipelineStagesCount"
        class="bg-blue-600 text-white px-4 py-1 rounded-l leading-6 hover:bg-blue-700 hover:text-white focus:outline-none disabled:bg-gray-600"
        :disabled="loading"
        @click="doMoveContactToNextPipelineStage"
      >
        <Loader v-if="loading" inverse small />
        Mark completed
      </button>

      <VDropdown right>
        <template #button="buttonProps">
          <button
            class="bg-gray-300 text-blue-600 px-4 py-1 rounded-r leading-6 hover:bg-blue-600 hover:text-white focus:outline-none disabled:text-gray-600"
            :class="[
              {
                'rounded-l': !pendingPipelineStagesCount,
              },
            ]"
            :disabled="loading"
            @click="buttonProps.open"
          >
            <Loader v-if="loading" small />
            Jump to
          </button>
        </template>

        <template #default="defaultProps">
          <VDropdownMenu>
            <button
              v-for="item in pipelineStages"
              :key="item.id"
              role="button"
              :class="[
                'block',
                'px-4',
                'py-2',
                'hover:bg-gray-200',
                'disabled:bg-blue-600',
                'disabled:text-white',
                'transition-color',
                'transition-fast',
                'text-right',
                'text-sm',
                'w-full',
              ]"
              :disabled="pipelineStage && pipelineStage.id === item.id"
              @click="defaultProps.close(() => doMoveContactToPipelineStage(item.id))"
            >
              {{ item.name }}
            </button>
          </VDropdownMenu>
        </template>
      </VDropdown>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VDropdownVue from '@/components/Common/VDropdown.vue';
import VDropdownMenuVue from '@/components/Common/VDropdownMenu.vue';

export default {
  components: {
    VDropdown: VDropdownVue,
    VDropdownMenu: VDropdownMenuVue,
  },

  props: {
    contactRequestId: {
      type: String,
      required: true,
    },
    pipelineStage: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState('entities', {
      contactRequest({ contactRequests }) {
        return contactRequests.byId[this.contactRequestId] || null;
      },
      pipeline({ pipelines }) {
        return pipelines.byId[this.pipelineStage.pipelineId] || null;
      },
      pipelineStages({ pipelineStages }) {
        if (!this.pipeline || !this.pipeline.pipelineStages) return [];
        return this.pipeline.pipelineStages.map((id) => pipelineStages.byId[id]);
      },
      contactRequestPipelineStages() {
        if (!this.contactRequest) return [];
        if (!this.contactRequest.pipelineStages) return [];
        if (!Array.isArray(this.contactRequest.pipelineStages)) return [];

        return this.contactRequest.pipelineStages;
      },
      pipelineStageIndex() {
        if (!this.pipelineStage) return null;

        return this.pipelineStages.findIndex(
          (pipelineStage) => pipelineStage.id === this.pipelineStage.id
        );
      },
      completedPipelineStages() {
        if (this.pipelineStageIndex === -1) return [];
        return this.pipelineStages.filter(
          (pipelineStage, index) => index < this.pipelineStageIndex
        );
      },
      completedPipelineStagesCount() {
        return this.completedPipelineStages.length;
      },
      pendingPipelineStages() {
        if (this.pipelineStageIndex === -1) return this.pipelineStages;

        return this.pipelineStages.filter(
          (pipelineStage, index) => index > this.pipelineStageIndex
        );
      },
      pendingPipelineStagesCount() {
        return this.pendingPipelineStages.length;
      },
      nextPipelineStage() {
        if (this.pendingPipelineStagesCount === 0) return null;
        return this.pendingPipelineStages[0];
      },
    }),
  },
  created() {
    this.fetchPipeline();
  },
  inject: ['refresh'],
  methods: {
    async fetchPipeline() {
      try {
        this.loading = true;

        await this.$store.dispatch('ui/pipelines/read', {
          pipelineUuid: this.pipelineStage.pipelineId,
          params: {
            include: 'pipelineStages',
          },
        });
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    isValidPipelineStageId(pipelineStageId) {
      return (
        this.pipelineStages.findIndex((pipelineStage) => pipelineStage.id === pipelineStageId) !==
        -1
      );
    },

    async doMoveContactToNextPipelineStage() {
      if (!this.nextPipelineStage) return;
      if (!this.nextPipelineStage.id) return;

      const { id: pipelineStageId } = this.nextPipelineStage;

      this.doMoveContactToPipelineStage(pipelineStageId);
    },

    async doMoveContactToPipelineStage(pipelineStageId) {
      if (!pipelineStageId) return;
      if (!this.isValidPipelineStageId(pipelineStageId)) return;
      if (this.contactRequest.pipelineStageId === pipelineStageId) return;

      try {
        this.loading = true;

        await this.$store.dispatch('ui/contactRequestPipelineStage/update', {
          id: this.contactRequest.id,
          formData: {
            pipelineStageId,
          },
        });

        await this.$store.commit('ui/contactRequestPipelineStage/setIdByParent', {
          parentId: this.contactRequest.id,
          id: pipelineStageId,
        });

        this.refresh();
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
